import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Music, Users, Heart, ArrowRight, Sparkles, Radio, Play } from "lucide-react";
import skzStudySession1 from "@/assets/skz-study-session-1.jpg";
import skzStudySession2 from "@/assets/skz-study-session-2.jpg";
import skzStudySession3 from "@/assets/skz-study-session-3.jpg";

const StudyWithSKZ = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Study With SKZ - Stray Kids Study Rooms"
        description="Study with Stray Kids fans worldwide. Join STAYs in virtual study rooms with SKZ playlists, Pomodoro timer, and productivity tools. Stay focused, stay motivated."
        keywords="study with stray kids, skz study room, stay study, kpop study room, stray kids playlist study"
        canonicalUrl="/study-with-skz/"
      />
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-background via-muted to-background" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-[120px] animate-glow-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-secondary/20 rounded-full blur-[120px] animate-glow-pulse" style={{ animationDelay: '1s' }} />
        
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-5xl mx-auto text-center space-y-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 animate-fade-in">
              <Music className="w-4 h-4 text-primary" />
              <span className="text-sm font-semibold text-primary">CSW STUDY TOGETHER</span>
            </div>
            
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight animate-fade-in" style={{ animationDelay: '0.1s' }}>
              Study With{" "}
              <span className="text-gradient">SKZ</span>
            </h1>
            
            <p className="text-xl sm:text-2xl text-muted-foreground max-w-3xl mx-auto font-light animate-fade-in" style={{ animationDelay: '0.2s' }}>
              Join a global community of STAYs who combine their passion for SKZ with productive study sessions. Stay focused, stay motivated, and study together.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4 animate-fade-in" style={{ animationDelay: '0.3s' }}>
              <Button 
                size="lg"
                className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-6 text-lg glow-primary transition-all hover:scale-105"
                asChild
              >
                <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                  Start A Room Now <ArrowRight className="ml-2 w-5 h-5" />
                </a>
              </Button>
            </div>
            
            {/* Video Section */}
            <div className="relative mt-12 animate-fade-in" style={{ animationDelay: '0.4s' }}>
              <div className="relative rounded-2xl overflow-hidden border-2 border-border/50 bg-card/30 backdrop-blur-sm shadow-2xl">
                <video 
                  autoPlay 
                  loop 
                  muted 
                  playsInline
                  className="w-full aspect-video object-cover"
                >
                  <source src="/videos/productivity-demo.mp4" type="video/mp4" />
                </video>
                <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-primary/90 flex items-center justify-center glow-primary">
                    <Play className="w-5 h-5 text-primary-foreground ml-1" />
                  </div>
                  <span className="text-foreground font-medium">See how it works</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Stray Kids */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-background" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto space-y-8">
            <h2 className="font-display text-4xl sm:text-5xl font-bold text-foreground text-center">
              Study with <span className="text-gradient">Stray Kids</span>
            </h2>
            <Card className="p-8 sm:p-12 bg-card/50 backdrop-blur-sm border-border/50">
              <div className="space-y-6 text-muted-foreground leading-relaxed text-lg">
                <p>
                  Stray Kids (often abbreviated as SKZ) is a South Korean boy group formed by JYP Entertainment in 2017. Known for their self-produced music, dynamic performances, and deeply relatable lyrics, Stray Kids have quickly ascended to global prominence. The group consists of eight members: Bang Chan, Lee Know, Changbin, Hyunjin, Han, Felix, Seungmin, and I.N.
                </p>
                <p>
                  Stray Kids' fanbase, known as STAY, is more than just a group of supporters. It's a global community bonded not just by a love for the music but by shared values, motivation, and connection.
                </p>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* The Rise of Study with SKZ */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute top-1/2 right-0 w-72 h-72 bg-primary/10 rounded-full blur-[100px]" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <h2 className="font-display text-4xl sm:text-5xl font-bold text-foreground">
                  The Rise of <span className="text-gradient">"Study with SKZ"</span>
                </h2>
                <p className="text-muted-foreground text-lg leading-relaxed">
                  As part of the growing trend of "study with K-Pop," Stray Kids fans have created spaces where they can combine their passion for SKZ with productive study sessions. These sessions often involve playing Stray Kids' music in the background or participating in virtual study rooms themed around the group.
                </p>
              </div>
              <Card className="overflow-hidden border-border/50 hover:border-primary/30 transition-all">
                <img 
                  src={skzStudySession2} 
                  alt="Stray Kids animated study session with members" 
                  className="w-full h-full object-cover"
                />
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-background" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto space-y-12">
            <div className="text-center space-y-4">
              <h2 className="font-display text-4xl sm:text-5xl font-bold text-foreground">
                CSW: The Ultimate Platform to <span className="text-gradient">Study with SKZ</span>
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                At CSW, we offer dedicated pages and study rooms where you can study with K-Pop idols, including Stray Kids.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {[
                { icon: <Music className="w-7 h-7" />, title: "Dedicated K-Pop Study Rooms", desc: "Themed study rooms for various K-Pop idols, including Stray Kids. Listen to curated playlists and meet fellow fans." },
                { icon: <Users className="w-7 h-7" />, title: "Connect with Fellow Fans", desc: "Meet others from around the world who are not just focused on their studies but are also enthusiastic about the same idols." },
                { icon: <Sparkles className="w-7 h-7" />, title: "Personalized Study Environments", desc: "From selecting your favorite SKZ tracks to setting the perfect background for concentration, CSW gives you the tools." },
                { icon: <Radio className="w-7 h-7" />, title: "Join the 'Study with K-Pop' Trend", desc: "Explore different study rooms, and even discover new groups or idols while staying focused on your academic goals." },
              ].map((feature, index) => (
                <Card key={index} className="p-8 bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all hover:scale-105 animate-fade-in" style={{ animationDelay: `${0.1 * index}s` }}>
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center text-primary mb-6">
                    {feature.icon}
                  </div>
                  <h3 className="font-display text-2xl font-semibold text-foreground mb-4">{feature.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{feature.desc}</p>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why CSW is Perfect */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute top-1/2 left-0 w-72 h-72 bg-secondary/10 rounded-full blur-[100px]" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto space-y-8">
            <h2 className="font-display text-4xl sm:text-5xl font-bold text-foreground text-center">
              Why CSW is Perfect for <span className="text-gradient">STAYs</span>
            </h2>
            <Card className="p-8 sm:p-12 bg-gradient-to-br from-primary/10 to-secondary/10 border-primary/20">
              <p className="text-muted-foreground leading-relaxed text-lg text-center">
                CSW is more than just a study platform; it's a place where fans of Stray Kids and other K-Pop groups can come together, stay motivated, and achieve their goals. Whether you're cramming for exams or just looking for a productive study environment, CSW's dedicated rooms allow you to study with K-Pop idols in a way that blends entertainment with efficiency.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Connect with Community */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-background" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <Card className="overflow-hidden order-2 lg:order-1 border-border/50 hover:border-primary/30 transition-all">
                <img 
                  src={skzStudySession3} 
                  alt="Stray Kids members in virtual study room" 
                  className="w-full h-full object-cover"
                />
              </Card>
              <div className="space-y-8 order-1 lg:order-2">
                <div className="flex items-center gap-2 mb-6">
                  <Heart className="w-8 h-8 text-primary fill-primary" />
                </div>
                <h2 className="font-display text-4xl sm:text-5xl font-bold text-foreground">
                  Connect with <span className="text-gradient">Like-Minded Fans</span>
                </h2>
                <div className="space-y-6 text-muted-foreground text-lg leading-relaxed">
                  <p>
                    CSW – Chill Study Work is more than just a study platform; it's a place to connect with like-minded individuals. Our virtual rooms bring fans together, creating a supportive community where they can study, chat, and share experiences.
                  </p>
                  <p>
                    Discover how studying with your favorite idols can transform your academic journey and make learning a truly enjoyable experience.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-secondary/5" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/10 rounded-full blur-[150px]" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              Ready to Study with <span className="text-gradient">SKZ?</span>
            </h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Join thousands of STAYs who are staying focused and motivated with Stray Kids. Start your study session today.
            </p>
            <Button 
              size="lg"
              className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-12 py-6 text-lg glow-primary transition-all hover:scale-105"
              asChild
            >
              <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                Explore CSW Rooms <ArrowRight className="ml-2 w-5 h-5" />
              </a>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default StudyWithSKZ;